import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import {
  login as apiLogin,
  logout as apiLogout,
  registerAccount as apiRegister,
  refreshAccessToken,
} from '../api/auth'
import { ApiError, setAccessToken } from '../api/client'
import type { AuthSession } from '../api/types'
import { installPreviewApi, isPreviewMode, previewSession } from '../preview/preview'
import { clearSession, loadStoredSession, saveSession } from './sessionStorage'

type RegisterInput = Parameters<typeof apiRegister>[0]

type AuthContextValue = {
  session: AuthSession | null
  loading: boolean
  login: (email: string, password: string, force?: boolean) => Promise<void>
  register: (input: RegisterInput) => Promise<void>
  logout: () => void
  setActiveOrganization: (organizationId: number) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return ctx
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<AuthSession | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const boot = async () => {
      // Демо в браузере без входа.
      if (isPreviewMode()) {
        installPreviewApi()
        const demo = previewSession()
        setAccessToken(demo.accessToken)
        if (!cancelled) {
          setSession(demo)
          setLoading(false)
        }
        return
      }

      const stored = loadStoredSession()
      if (!stored) {
        if (!cancelled) {
          setLoading(false)
        }
        return
      }

      setAccessToken(stored.accessToken)

      if (stored.refreshToken) {
        try {
          const tokens = await refreshAccessToken(stored.refreshToken)
          const next: AuthSession = {
            ...stored,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken ?? stored.refreshToken,
          }
          setAccessToken(next.accessToken)
          saveSession(next)
          if (!cancelled) {
            setSession(next)
          }
        } catch (error) {
          if (error instanceof ApiError && error.status === 401) {
            clearSession()
            setAccessToken(null)
          } else if (!cancelled) {
            // Сеть моргнула — работаем на текущем access-токене.
            setSession(stored)
          }
        }
      } else if (!cancelled) {
        setSession(stored)
      }

      if (!cancelled) {
        setLoading(false)
      }
    }

    void boot()
    return () => {
      cancelled = true
    }
  }, [])

  const login = useCallback(async (email: string, password: string, force = false) => {
    const next = await apiLogin(email, password, force)
    setAccessToken(next.accessToken)
    saveSession(next)
    setSession(next)
  }, [])

  const register = useCallback(async (input: RegisterInput) => {
    const next = await apiRegister(input)
    setAccessToken(next.accessToken)
    saveSession(next)
    setSession(next)
  }, [])

  const logout = useCallback(() => {
    void apiLogout(session)
    clearSession()
    setAccessToken(null)
    setSession(null)
  }, [session])

  const setActiveOrganization = useCallback((organizationId: number) => {
    setSession((current) => {
      if (!current || current.organizationId === organizationId) {
        return current
      }
      const next = { ...current, organizationId }
      saveSession(next)
      return next
    })
  }, [])

  const value = useMemo(
    () => ({ session, loading, login, register, logout, setActiveOrganization }),
    [session, loading, login, register, logout, setActiveOrganization],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
